import { httpsCallable } from 'firebase/functions';
import { ref, uploadBytesResumable } from 'firebase/storage';
import { getFirebaseFunctions, getFirebaseStorage } from './firebase';

const LAST_CODE_KEY = 'photociancio.lastCode';
const MAX_FILES = 2000;

export async function createUploadSession() {
  const createSession = httpsCallable(getFirebaseFunctions(), 'createUploadSession');
  const result = await createSession();
  const session = result.data;

  if (!session?.code || !session?.uploadPrefix) {
    throw new Error('Firebase did not return a valid upload session.');
  }

  return session;
}

export async function finalizeUpload(session, images, onProgress) {
  if (!session?.code || !session?.uploadPrefix) {
    throw new Error('Upload session was not created.');
  }

  if (images.length > MAX_FILES) {
    throw new Error(`Upload cannot exceed ${MAX_FILES} files.`);
  }

  const uploadItems = images.flatMap((image) => {
    const items = [{ id: `${image.id}:original`, bytes: image.file.size || 0 }];

    if (image.editedBlob) {
      items.push({ id: `${image.id}:customer-edited`, bytes: image.editedBlob.size || 0 });
    }

    return items;
  });
  const progressByItem = new Map(uploadItems.map((item) => [item.id, { loaded: 0, total: item.bytes }]));
  const totalBytes = uploadItems.reduce((sum, item) => sum + item.bytes, 0);
  const totalImages = images.length;
  let completedImages = 0;

  const emitProgress = () => {
    const uploadedBytes = Array.from(progressByItem.values()).reduce((sum, item) => sum + Math.min(item.loaded, item.total), 0);
    const progress = totalBytes ? Math.round((uploadedBytes / totalBytes) * 100) : 0;

    onProgress?.({
      completedImages,
      currentImage: Math.min(completedImages + 1, totalImages),
      progress,
      totalImages,
      totalBytes,
      uploadedBytes,
    });
  };

  const reportProgress = (itemId, loaded, total) => {
    progressByItem.set(itemId, { loaded, total });
    emitProgress();
  };

  const metadata = [];

  for (const image of images) {
    const originalPath = `${session.uploadPrefix}/${image.id}/original`;
    await uploadFile(originalPath, image.file, `${image.id}:original`, reportProgress);

    let hasCustomerEdit = false;
    if (image.editedBlob) {
      const editedPath = `${session.uploadPrefix}/${image.id}/customer-edited`;
      await uploadFile(editedPath, image.editedBlob, `${image.id}:customer-edited`, reportProgress);
      hasCustomerEdit = true;
    }

    metadata.push({
      id: image.id,
      filename: image.file.name,
      contentType: image.file.type || 'application/octet-stream',
      size: image.file.size,
      hasCustomerEdit,
      editState: image.editState,
    });

    completedImages += 1;
    emitProgress();
  }

  const finalize = httpsCallable(getFirebaseFunctions(), 'finalizeUpload');
  const result = await finalize({
    code: session.code,
    images: metadata,
  });

  localStorage.setItem(LAST_CODE_KEY, session.code);

  return {
    ...result.data,
    code: session.code,
  };
}

export function getRecentUploadCode() {
  return localStorage.getItem(LAST_CODE_KEY);
}

function uploadFile(path, fileOrBlob, itemId, reportProgress) {
  return new Promise((resolve, reject) => {
    const storageRef = ref(getFirebaseStorage(), path);
    const uploadTask = uploadBytesResumable(storageRef, fileOrBlob, {
      contentType: fileOrBlob.type || 'application/octet-stream',
    });

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        reportProgress(itemId, snapshot.bytesTransferred, snapshot.totalBytes || fileOrBlob.size || 0);
      },
      reject,
      () => resolve(uploadTask.snapshot),
    );
  });
}
